import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import type { NextPage } from 'next';

import { Breadcrumb } from 'ui/components/Breadcrumb';
import { ProductList } from 'ui/components/ProductList';
import { Page } from 'ui/components/Page';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { ProductListItem } from 'ui/components/ProductListItem';
import { useTranslate } from 'hooks/useTranslations';
import { LoadMore } from 'ui/components/LoadMore';
import { NoAnalyticsNoSearchResults } from 'ui/components/NoSearchResults';
import { PageHead } from 'ui/components/PageHead';
import { ScrollToTopButton } from 'ui/elements/ScrollToTopButton';
import {
  ProductSearchQueryState,
  useProductSearchQuery,
} from 'gql/queries/search';
import { MAX_PRODUCT_PAGE_SIZE } from 'utils/products';
import { ProductListFilterProvider } from 'ui/components/ProductListFilterProvider';
import {
  searchAnalyticsEvent,
  handleIntersectionPLP,
  ProductSearchHitWithIndex,
} from 'utils/analytics';
import { ProductSearchHit } from '__generated__/graphql';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { useScreenSize } from 'hooks/useScreenSize';
import { decodeFilters } from 'utils/productFilters';

type PaginatedPageProps = {
  offset: number;
  isFirst: boolean;
  isLast: boolean;
  totalCount: number;
  productSearchResult: ProductSearchQueryState | null;
  productsRef: MutableRefObject<ProductSearchHit[]>;
  allProductsRef: MutableRefObject<ProductSearchHit[]>;
  handleIntersection: (productId: string) => void;
  setListProducts: React.Dispatch<React.SetStateAction<ProductSearchHit[]>>;
};

const PaginatedPage = ({
  offset,
  isLast,
  totalCount,
  productSearchResult,
  productsRef,
  allProductsRef,
  handleIntersection,
  setListProducts,
}: PaginatedPageProps) => {
  const router = useRouter();

  const [productSearchPaginated] = useProductSearchQuery({
    pause: productSearchResult !== null,
    router,
    offset,
    includeMetadata: false,
  });
  // Determine whether to use the supplied queries (for the first page or results) or the
  // queries executed in this component
  const query = productSearchResult ?? productSearchPaginated;

  const products = React.useMemo(
    () =>
      query.data?.search.nodes.filter(
        ({ masterProduct }) => masterProduct !== null
      ) || [],
    [query.data?.search.nodes]
  );

  const prevProductsRef = useRef<ProductSearchHit[]>([]);

  useEffect(() => {
    const newProducts = products.filter(
      product =>
        !prevProductsRef.current.some(
          p => p?.variantProduct?.id === product?.variantProduct?.id
        )
    );

    if (newProducts.length > 0) {
      setListProducts(prevProducts => {
        const updatedProducts = [...prevProducts, ...newProducts];
        prevProductsRef.current = updatedProducts;
        return updatedProducts;
      });
    }
  }, [products, setListProducts]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '1px',
      threshold: 0.5,
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const productId = entry.target.getAttribute('data-product-id');
          const dataTestId = entry.target.getAttribute('data-test-id');
          handleIntersection(productId || dataTestId!);
        }
      });
    }, options);

    document
      .querySelectorAll('[data-test-id="product-list-item"]')
      .forEach(item => observer.observe(item));

    const styliticsPromoTile = document.querySelector(
      '[data-test-id="positioned-promo-tile"]'
    );
    if (styliticsPromoTile) observer.observe(styliticsPromoTile);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersection, productsRef, allProductsRef]);

  return (
    <>
      {!query.fetching &&
        products.map((p, i) => (
          <ProductListItem
            key={p.id}
            {...p}
            position={offset + i + 1}
            search={query?.operation?.variables.q}
          />
        ))}
      <LoadMore
        fetching={query.fetching}
        isLastPageCurrentlyDisplayed={isLast}
        totalCount={totalCount}
      />
    </>
  );
};

const ProductSearch: NextPage = () => {
  const router = useRouter();
  const { localizeUrlPath, isLocalizedFeatureFlagEnabled } = useSiteConfig();
  const t = useTranslate();

  const searchTerm = (router.query.q as string) || '';

  const intersectedProductsRef = useRef<ProductSearchHitWithIndex[]>([]);
  const { pageviewEventHasFired } = usePageEventsContext();
  const { currency } = useSiteConfig();
  const intersectAllProductsRef = useRef<ProductSearchHitWithIndex[]>([]);
  const screenSize = useScreenSize();
  const [listProducts, setListProducts] = useState<ProductSearchHit[]>([]);

  const handleIntersection = useCallback(
    (productId: string) => {
      handleIntersectionPLP({
        productId,
        intersectedProductsRef,
        intersectAllProductsRef,
        listProducts,
        screenSize,
        currency,
        offset,
        pageviewEventHasFired,
        searchTerm,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listProducts, screenSize, pageviewEventHasFired]
  );
  const routeShards = router.asPath.split('?');
  const queryString = routeShards?.[1] || '';
  const newQueryParams = useMemo(
    () => new URLSearchParams(queryString),
    [queryString]
  );

  const queryParams = useMemo(() => {
    return Array.from(newQueryParams).reduce((result, [key, value]) => {
      result[key] = value;
      return result;
    }, {});
  }, [newQueryParams]);
  const validFilters = decodeFilters(queryParams);
  const hasFilterSelected = Boolean(Object.keys(validFilters).length);

  const [productSearchResult] = useProductSearchQuery({
    pause: !searchTerm,
    router,
    offset: 0,
    includeMetadata: true,
  });

  const path = React.useMemo(() => {
    return [
      { label: 'home', url: '' },
      { label: searchTerm, url: '' },
    ];
  }, [searchTerm]);

  useEffect(() => {
    // This console is for QAs to test the feature flag sytem
    if (
      searchTerm === 'feature_flag_qa_test' &&
      isLocalizedFeatureFlagEnabled('QA_TEST_FLAG')
    ) {
      // eslint-disable-next-line no-console
      console.log('Hi, QA 👋!');
    }
  }, [isLocalizedFeatureFlagEnabled, searchTerm]);

  useEffect(() => {
    if (!searchTerm) {
      // No search term was supplied so redirect to home
      router.replace(localizeUrlPath('/'));
    }
  }, [router, searchTerm, localizeUrlPath]);

  const searchResults = productSearchResult.data?.search;
  const totalCount = searchResults?.totalCount || 0;
  const queryOffset = Number(router.query.offset) || 0;
  const offset =
    totalCount && queryOffset >= totalCount
      ? totalCount - MAX_PRODUCT_PAGE_SIZE
      : queryOffset;

  useEffect(() => {
    if (!productSearchResult.fetching) searchAnalyticsEvent(`${totalCount}`);
  }, [totalCount, productSearchResult.fetching]);

  if (!searchTerm) return null;

  const list = new Array(queryOffset / MAX_PRODUCT_PAGE_SIZE + 1).fill(1);

  const isStale = productSearchResult?.operation?.variables?.q !== searchTerm;

  const noResults =
    !productSearchResult.fetching && searchResults?.nodes.length === 0;

  const showLoadingIndicator =
    isStale ||
    (offset !== 0 && productSearchResult.fetching && !productSearchResult.data);

  return (
    <>
      <PageHead title={`${t('searchResult')}: ${searchTerm}`} />
      <Page
        query={{
          ...productSearchResult,
          fetching: showLoadingIndicator,
        }}
        bloomreachData={{
          search_term: searchTerm,
        }}
      >
        <ProductListFilterProvider
          noResults={noResults}
          filteringOptions={searchResults?.filteringOptions || []}
          sortingOptions={searchResults?.sortingOptions || []}
          defaultSortOption={searchResults?.selectedSort || ''}
          isFetching={productSearchResult.fetching}
          totalCount={totalCount}
          search={router.query.q as string}
        >
          {noResults && !hasFilterSelected ? (
            <NoAnalyticsNoSearchResults />
          ) : (
            <>
              <Breadcrumb path={path} />
              <ProductList
                noResults={noResults}
                filteringOptions={searchResults?.filteringOptions || []}
                sortingOptions={searchResults?.sortingOptions || []}
                defaultSortOption={searchResults?.selectedSort || ''}
                isFetching={productSearchResult.fetching}
                totalCount={totalCount}
                search={router.query.q as string}
              >
                {list.map((_, i) => (
                  <PaginatedPage
                    isFirst={i === 0}
                    isLast={i === list.length - 1}
                    key={i}
                    offset={i * MAX_PRODUCT_PAGE_SIZE}
                    totalCount={totalCount}
                    productSearchResult={i === 0 ? productSearchResult : null}
                    productsRef={intersectedProductsRef}
                    allProductsRef={intersectAllProductsRef}
                    handleIntersection={handleIntersection}
                    setListProducts={setListProducts}
                  />
                ))}
              </ProductList>
            </>
          )}
        </ProductListFilterProvider>
        <ScrollToTopButton />
      </Page>
    </>
  );
};

export default ProductSearch;
